import React, { FC, memo, useEffect, useRef, useState } from "react";
import { Box, InputBase, Stack } from "@mui/material";
import { useDrop } from "react-dnd";
import {
  buildVisualList,
  normalizeReviewerOrders,
} from "utils/signerOrderUtils";
import { RequestParticipant, Reviewers } from "models/interfaces";
import {
  mergeReviewerToGroup,
  moveReviewerBlock,
  moveReviewerGroup,
  updateReviewerOrders,
} from "redux/appSlice";
import { useDispatch } from "react-redux";

type GroupInputProps = {
  group: RequestParticipant[];
  index: number;
  reviewers: RequestParticipant[];
};

export const ReviewerGroupOrderInput: FC<GroupInputProps> = memo(
  ({ group, index, reviewers }) => {
    const dispatch = useDispatch();
    const representative = group[0];

    // Use store order as source of truth; index+1 can stay stale after merge/normalize
    const storeOrder =
      representative?.order != null ? Number(representative.order) : index + 1;

    const [value, setValue] = useState(String(storeOrder));

    // Sync from store when order changes (e.g. DnD, external updates)
    useEffect(() => {
      setValue(String(storeOrder));
    }, [storeOrder]);

    const handleCommit = () => {
      const trimmed = value.trim();
      // block empty / zero / non-number
      if (trimmed === "" || isNaN(Number(trimmed)) || Number(trimmed) <= 0) {
        setValue(String(storeOrder)); // restore previous value
        return;
      }

      const newOrder = Number(trimmed);

      const movingMembers = group;
      const targetMembers = reviewers.filter((r) => r.order === newOrder);

      // find any existing group from BOTH sides
      const existingGroup =
        targetMembers.find((r) => r.group)?.group ??
        movingMembers.find((r) => r.group)?.group;

      const updated = reviewers.map((r) => {
        const isMoving = movingMembers.some((g) => g.id === r.id);
        if (!isMoving) return r;

        return {
          ...r,
          order: newOrder,
          group: existingGroup,
        };
      });

      const normalized = normalizeReviewerOrders(updated);

      // After merge/normalize, store order can change while index stays same.
      // Update local value immediately so UI matches Redux.
      const committedOrder = normalized.find((r) =>
        movingMembers.some((m) => m.id === r.id),
      )?.order;
      if (committedOrder != null) {
        setValue(String(committedOrder));
      }

      dispatch(updateReviewerOrders(normalized));
    };

    return (
      <InputBase
        sx={{
          width: 25,
          height: 25,
          borderRadius: "4px",
          backgroundColor: representative?.color,
          fontSize: "12px",
          fontWeight: "bold",
          textAlign: "center",
          "& .MuiInputBase-input": {
            textAlign: "center",
            color: "white",
          },
        }}
        value={value}
        inputProps={{ maxLength: 2 }}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
            handleCommit();
          }
        }}
      />
    );
  },
);

const ItemType = {
  APPROVER: "approver",
};

type Props = {
  orderKey: number;
  index: number;
  group: any[];
  children: React.ReactNode;
  orderNeeded?: boolean;
  allReviewers?: any;
  dispatch: any;
};

export const ReviewerDropContainer = React.memo(function ReviewerDropContainer({
  orderKey,
  index,
  group,
  children,
  orderNeeded,
  allReviewers,
  dispatch,
}: Props) {
  const dropRef = useRef<HTMLDivElement | null>(null);
  const lastProcessedMove = useRef<string | null>(null);

  // reviewer grouping rule (IMPORTANT)
  // const isTargetGroup = Boolean(group?.[0]?.groupId);

  const [, drop] = useDrop({
    accept: ItemType.APPROVER,

    hover: (dragged: any, monitor: any) => {
      if (!dropRef.current) return;
      if (dragged.justMoved) return;

      const targetOrder = Number(orderKey);
      const fromOrder = dragged.order;

      // same block → do nothing
      if (fromOrder === targetOrder) return;

      // element bounds
      const rect = dropRef.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;

      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const cursorY = clientOffset.y - rect.top;

      /* Only move when cursor crosses half. This prevents reorder oscillation */
      if (fromOrder < targetOrder && cursorY < middleY) return;
      if (fromOrder > targetOrder && cursorY > middleY) return;

      // already handled this target
      if (dragged.lastHoverOrder === targetOrder) return;
      dragged.lastHoverOrder = targetOrder;

      const isDraggedGroup = dragged.dragType === "GROUP";

      /* GROUP → ANYWHERE */
      if (isDraggedGroup) {
        dispatch(
          moveReviewerGroup({
            reviewerIds: dragged.groupReviewerIds,
            toOrder: targetOrder,
          }),
        );

        dragged.order = targetOrder;

        dragged.justMoved = true;
        requestAnimationFrame(() => {
          dragged.justMoved = false;
        });
        return;
      }

      /* SINGLE → SINGLE (REORDER) */
      if (!isDraggedGroup) {
        dispatch(
          moveReviewerBlock({
            reviewerId: dragged.id,
            toOrder: targetOrder,
          }),
        );
        dragged.order = targetOrder;

        dragged.justMoved = true;
        requestAnimationFrame(() => {
          dragged.justMoved = false;
        });
        return;
      }
    },

    drop: (dragged: any) => {
      lastProcessedMove.current = null;
      const targetIndex = index;
      const targetReviewer = group?.[0];
      const isTargetGrouped = group?.[0]?.group;
      const targetOrder = Number(orderKey);
      // SINGLE → GROUP (MERGE)
      if (dragged.dragType === "SINGLE" && isTargetGrouped) {
        dispatch(
          mergeReviewerToGroup({
            draggedId: dragged.id,
            targetId: targetReviewer.id,
          }),
        );
        const updated = normalizeReviewerOrders(
          allReviewers.map((r: Reviewers) =>
            r.id === dragged.id ? { ...r, order: targetOrder } : r,
          ),
        );

        dispatch(updateReviewerOrders(updated));
        return;
      }
      dragged.blockIndex = targetIndex;
    },
  });

  useEffect(() => {
    if (dropRef.current) drop(dropRef.current);
  }, [drop]);

  useEffect(() => {
    return () => {
      lastProcessedMove.current = null;
    };
  }, []);

  return (
    <Stack
      direction="row"
      alignItems="stretch"
      spacing={1}
      sx={{ width: "100%", marginTop: "0px !important" }}
    >
      {orderNeeded && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ReviewerGroupOrderInput
            reviewers={allReviewers}
            group={group}
            index={index}
          />
        </Box>
      )}
      <Box
        ref={dropRef}
        sx={{
          width: "100%",
          borderRadius: "8px",
          "&:hover": { boxShadow: 2 },
          cursor: "move",
          marginTop: "5px !important",
        }}
      >
        {children}
      </Box>
    </Stack>
  );
});

type StableReviewerEmptyDropZoneProps = {
  reviewers: Reviewers[];
  dispatch: any;
  baseOrder: number;
  isLast?: boolean;
};

const removeGroup = (r: Reviewers): Reviewers => {
  const { group, groupId, ...rest } = r;
  return rest;
};

export const StableReviewerEmptyDropZone = React.memo(
  function StableReviewerEmptyDropZone({
    reviewers,
    dispatch,
    baseOrder,
    isLast,
  }: StableReviewerEmptyDropZoneProps) {
    const [{ canDrop, draggedType }, drop] = useDrop({
      accept: ItemType.APPROVER,
      canDrop: (dragged: any) => dragged.dragType === "GROUP_MEMBER",
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        draggedType: monitor.getItem()?.dragType,
      }),
      drop: (dragged: any) => {
        if (dragged.dragType !== "GROUP_MEMBER") return;

        const visual = buildVisualList(reviewers);

        // index where user dropped (this dropzone is BEFORE baseOrder)
        const insertIndex = visual.findIndex((v) => v.order === baseOrder);

        const draggedReviewer = reviewers.find(
          (s) => s.id === dragged.signerId,
        )!;

        const rebuilt: Reviewers[] = [];

        let currentOrder = 1;

        visual.forEach((block, i) => {
          if (i === insertIndex) {
            // insert UNGROUPED signer
            rebuilt.push({
              ...removeGroup(draggedReviewer),
              order: currentOrder++,
            });
          }

          // push existing block members
          block.members
            .filter((m: any) => m.id !== dragged.signerId)
            .forEach((m: any) => {
              rebuilt.push({
                ...m,
                order: currentOrder,
              });
            });

          if (block.members.some((m: any) => m.id !== dragged.signerId)) {
            currentOrder++;
          }
        });

        if (insertIndex === -1) {
          rebuilt.push({
            ...removeGroup(draggedReviewer),
            order: currentOrder,
          });
        }
        dispatch(updateReviewerOrders(rebuilt));
      },
    });

    const isActive = canDrop && draggedType === "GROUP_MEMBER";

    let dropHeight = 0;

    if (isActive) {
      dropHeight = isLast ? 65 : 30;
    }

    return (
      <Box
        ref={drop}
        sx={{
          height: dropHeight,
          opacity: isActive ? 1 : 0,
          transition: "all 0.15s ease",
          width: "100%",
          borderRadius: 4,
          backgroundColor: "transparent",
        }}
      />
    );
  },
);
